//* 코치 컨텐츠 관련 API
//  2025.01.27
//  희문

import * as BaseApi from "api/Base.api";
import { IPagination } from "api/interfaces/common.interface";
import axios from "axios";
import { getQueryStringFromObject } from "helpers/Api.helper";

//* 컨텐츠 목록 조회
//  2025.01.27
//  희문
export interface IFetchContentListParams {
    page?: number;
    limit?: number;
    isActivated?: boolean;
}

export interface IContentMultiText {
    id: number;
    contentId: number;
    userId?: string;
    languageCode: string;
    title?: string;
    desc: string;
    language?: {
        code: string;
        names: string;
    };
}

export interface IContentVideo {
    id: number;
    contentId: number;
    languageCode: string;
    videoId: string;
    language?: {
        code: string;
        names: string;
    };
    video?: {
        id: string;
        name: string;
        subUrl: string;
        thumbnailImageId?: string;
        isPublic?: boolean;
        thumbnailImage?: {
            id: string;
            name: string;
            subUrl: string;
            isPublic?: boolean;
        };
    };
}

export interface IReactionCounts {
    veryHelpful: number;
    helpful: number;
    disappointed: number;
}

export interface IContent {
    id: number;
    userId: string;
    title?: string;
    desc?: string;
    youtubeUrl?: string;
    // 영상은 contentVideos에서 관리 (ko 포함)
    viewCount?: number;
    commentCount?: number;
    reactionCount?: number;
    favoriteCount?: number;
    isActivated: boolean;
    createdAt: string;
    updatedAt: string;
    video?: {
        id: string;
        name: string;
        subUrl: string;
        thumbnailImageId?: string;
        isPublic?: boolean;
        thumbnailImage?: {
            id: string;
            name: string;
            subUrl: string;
            isPublic?: boolean;
        };
    };
    user?: {
        id: string;
        username: string;
    };
    multiTexts?: IContentMultiText[];
    videos?: IContentVideo[];
    reactionCounts?: IReactionCounts;
}

export interface IFetchContentListResponse {
    contentList: IContent[];
    pagination: IPagination;
}

export const fetchContentList = async (params: IFetchContentListParams, signal?: AbortSignal): Promise<IFetchContentListResponse> => {
    const url = "/koach/v2/content";
    // signal이 제공된 경우 axios를 직접 사용하여 요청 취소 지원
    if (signal) {
        const queryParams = getQueryStringFromObject(params);
        const response = await axios.get(`${url}${queryParams}`, { signal });
        if (response.status === 200) {
            return response.data.data;
        }
        throw new Error('Failed to fetch content list');
    }
    // signal이 없는 경우 기존 BaseApi.get 사용
    return await BaseApi.get(url, params);
};

//* 컨텐츠 상세 조회
//  2025.01.27
//  희문
export interface IFetchContentDetailResponse {
    content: IContent;
}

export const fetchContentDetail = async (id: number): Promise<IFetchContentDetailResponse> => {
    const url = `/koach/v2/content/${id}`;
    return await BaseApi.get(url);
};

//* 컨텐츠 생성
//  2025.01.27
//  희문
export interface ICreateContentParams {
    title?: string;
    desc?: string;
    videoList?: Array<{ languageCode: string; videoId: string }>;
    multiTexts?: Array<{ languageCode: string; userId?: string; title?: string; text: string }>;
}

export interface ICreateContentResponse {
    contentId: number;
}

export const createContent = async (params: ICreateContentParams): Promise<ICreateContentResponse> => {
    const url = "/koach/v2/content";
    const response = await BaseApi.post(url, params);
    if (!response) {
        throw new Error('컨텐츠 생성에 실패했습니다.');
    }
    return response.data.data;
};

//* 컨텐츠 수정
//  2025.01.27
//  희문
export interface IUpdateContentParams {
    title?: string;
    desc?: string;
    videoList?: Array<{ languageCode: string; videoId: string }>;
    multiTexts?: Array<{ languageCode: string; userId?: string; title?: string; text: string }>;
}

export interface IUpdateContentResponse {
    success: boolean;
}

export const updateContent = async (id: number, params: IUpdateContentParams): Promise<IUpdateContentResponse> => {
    const url = `/koach/v2/content/${id}`;
    const response = await BaseApi.put(url, params);
    if (!response) {
        throw new Error('컨텐츠 수정에 실패했습니다.');
    }
    return response.data;
};

//* 컨텐츠 삭제
//  2025.01.27
//  희문
export interface IDeleteContentResponse {
    success: boolean;
}

export const deleteContent = async (id: number): Promise<IDeleteContentResponse> => {
    const url = `/koach/v2/content/${id}`;
    const response = await BaseApi.remove(url);
    if (!response) {
        throw new Error('컨텐츠 삭제에 실패했습니다.');
    }
    return response.data;
};

//* 컨텐츠 활성화/비활성화
//  2025.01.27
//  희문
export interface IToggleActivateParams {
    isActivated: boolean;
}

export interface IToggleActivateResponse {
    success: boolean;
    isActivated: boolean;
}

export const toggleActivate = async (id: number, isActivated: boolean): Promise<IToggleActivateResponse> => {
    const url = `/koach/v2/content/${id}/activate`;
    const response = await BaseApi.patch(url, { isActivated });
    if (!response) {
        throw new Error('컨텐츠 상태 변경에 실패했습니다.');
    }
    return response.data;
};

//* 콘텐츠 댓글 목록 조회
//  2025.01.27
//  희문
export interface IContentComment {
    id: number;
    contentId: number;
    userId: string;
    parentCommentId?: number;
    text: string;
    contents?: string; // CommentItem 호환성
    likeCount: number;
    isLiked?: boolean;
    createdAt: string;
    updatedAt: string;
    user?: any;
    childList?: IContentComment[];
}

export interface IContentCommentsResult {
    commentList: IContentComment[];
    pagination: {
        currentPage: number;
        totalCount: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

export const getContentComments = async (contentId: number, params?: {
    page?: number;
    limit?: number;
}): Promise<IContentCommentsResult> => {
    try {
        const response = await BaseApi.get(`/koach/v2/content/${contentId}/comment`, params);
        return response as IContentCommentsResult;
    } catch (error) {
        console.error("콘텐츠 댓글 목록 조회 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 작성
//  2025.01.27
//  희문
export interface ICreateContentCommentRequest {
    text: string;
    parentId?: number;
}

export interface ICreateContentCommentResponse {
    comment: IContentComment;
}

export const createContentComment = async (contentId: number, text: string, parentId?: number): Promise<ICreateContentCommentResponse> => {
    try {
        const data: ICreateContentCommentRequest = {
            text: text,
            parentId: parentId
        };
        const response = await BaseApi.post2(`/koach/v2/content/${contentId}/comment`, data);
        return response as ICreateContentCommentResponse;
    } catch (error) {
        console.error("콘텐츠 댓글 작성 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 삭제
//  2025.01.27
//  희문
export interface IDeleteContentCommentResponse {
    success: boolean;
}

export const deleteContentComment = async (contentId: number, commentId: number): Promise<IDeleteContentCommentResponse> => {
    try {
        await BaseApi.remove(`/koach/v2/content/${contentId}/comment/${commentId}`);
        return { success: true };
    } catch (error) {
        console.error("콘텐츠 댓글 삭제 실패:", error);
        throw error;
    }
};

//* 콘텐츠 댓글 좋아요 토글
//  2025.01.27
//  희문
export interface IToggleContentCommentLikeResponse {
    isLiked: boolean;
    likeCount: number;
}

export const toggleContentCommentLike = async (contentId: number, commentId: number): Promise<IToggleContentCommentLikeResponse> => {
    try {
        const response = await BaseApi.post2(`/koach/v2/content/${contentId}/comment/${commentId}/like`);
        return response as IToggleContentCommentLikeResponse;
    } catch (error) {
        console.error("콘텐츠 댓글 좋아요 토글 실패:", error);
        throw error;
    }
};
